
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { ArrowRight, Activity, ArrowRight as CircleArrowRight, Brain, Stethoscope, Award, Check, Calendar, FlaskConical } from 'lucide-react';

const HeroSection = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Updated flow steps showing the process from behavior signals to outcomes
  const flowSteps = [
    { 
      icon: <Activity className="h-6 w-6 text-morgenBlue" />,
      label: "Behavior Signals",
      subtext: "Daily actions, movement, context cues"
    },
    { 
      icon: <CircleArrowRight className="h-5 w-5 text-gray-400" />,
      label: "",
      subtext: ""
    },
    { 
      icon: <Brain className="h-6 w-6 text-morgenTeal" />,
      label: "Personalized Nudge Engine",
      subtext: "Adaptive, LLM-powered recommendations"
    },
    { 
      icon: <CircleArrowRight className="h-5 w-5 text-gray-400" />,
      label: "",
      subtext: ""
    },
    { 
      icon: <Stethoscope className="h-6 w-6 text-morgenViolet" />,
      label: "Care Activation",
      subtext: "Labs, screenings, follow-ups, referrals"
    },
    { 
      icon: <CircleArrowRight className="h-5 w-5 text-gray-400" />,
      label: "",
      subtext: ""
    },
    { 
      icon: <Award className="h-6 w-6 text-morgenPurple" />,
      label: "Clinical & Financial Outcomes",
      subtext: "Risk score shift, engagement, PMPM impact"
    }
  ];
  
  return (
    <section className="py-20 lg:py-28 overflow-hidden relative bg-white">
      {/* More subtle background accents */}
      <div className="absolute -top-64 -left-64 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-morgenBlue/5 to-morgenTeal/5 blur-3xl"></div>
      <div className="absolute -bottom-64 -right-64 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-morgenViolet/5 to-morgenPurple/5 blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <div className="lg:w-6/12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-2 text-morgenDarkCharcoal">
                Empowering Smarter, Personalized Care
              </h1>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight flex items-center gap-3 mb-6">
                <span className="inline-block w-12 h-2 bg-morgenDarkCharcoal rounded-full"></span>
                <span className="text-morgenDarkCharcoal">at Scale.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                Morgen uses agentic AI to surface hidden risk, personalize nudges, and activate reimbursable care — 
                reducing PMPM and closing clinical gaps across value-based populations.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => {
                    const whatWeDoSection = document.getElementById('what-we-do');
                    whatWeDoSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="bg-morgen-gradient hover:opacity-90 text-white"
                  size="lg"
                >
                  See How It Works
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contact')}
                  className="border-morgenCharcoal text-morgenCharcoal hover:bg-morgenCharcoal/5"
                  size="lg"
                >
                  Talk to Our Team
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12"
            >
              <p className="text-sm tracking-wide uppercase text-gray-500 font-medium mb-4">INFRASTRUCTURE FOR BETTER CARE</p>
              <p className="text-xl md:text-2xl text-morgenDarkCharcoal font-medium leading-relaxed">
                "From behavior to care. From nudges to outcomes. From signals to savings."
              </p>
            </motion.div>
          </div>
          
          <motion.div 
            className="lg:w-6/12"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex flex-col items-center mb-8">
                  <h3 className="text-xl md:text-2xl font-bold mb-2 text-morgenCharcoal text-center">
                    Morgen's Clinical Activation System
                  </h3>
                  <p className="text-gray-600 text-center max-w-lg">
                    Our agentic infrastructure connects health signals to clinical action
                  </p>
                </div>
                
                {/* Flow diagram with labels and subtexts - improved for mobile */}
                {isMobile ? (
                  // Mobile version - vertical layout
                  <div className="flex flex-col gap-8 mb-10">
                    {flowSteps.filter(step => step.label).map((step, index) => (
                      <div key={index} className="flex flex-col items-center">
                        <div className="flex justify-center items-center h-12 mb-2">
                          {step.icon}
                        </div>
                        <span className="text-sm font-bold text-center mb-1">{step.label}</span>
                        <span className="text-xs text-gray-500 text-center">{step.subtext}</span>
                        
                        {/* Add down arrow for all but the last item */}
                        {index < flowSteps.filter(s => s.label).length - 1 && (
                          <div className="my-2">
                            <ArrowRight className="h-4 w-4 text-gray-400 rotate-90" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop version - horizontal layout 
                  <div className="flex flex-wrap justify-center items-center gap-4 mb-10">
                    {flowSteps.map((step, index) => (
                      <React.Fragment key={index}>
                        <div className="flex flex-col items-center max-w-[120px]">
                          <div className="flex justify-center items-center h-12 mb-2">
                            {step.icon}
                          </div>
                          {step.label && (
                            <>
                              <span className="text-sm font-bold text-center mb-1">{step.label}</span>
                              {step.subtext && (
                                <span className="text-xs text-gray-500 text-center">{step.subtext}</span>
                              )}
                            </>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}

                {/* Outcome metrics without specific numbers - fixed for mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  <div className="text-center p-4 bg-blue-50/30 rounded-lg border border-blue-100/30">
                    <div className="flex justify-center mb-2">
                      <FlaskConical className="h-6 w-6 text-morgenBlue" />
                    </div>
                    <p className="text-lg font-bold text-morgenBlue mb-1">Care Activations</p>
                    <p className="text-sm text-gray-600">Improved engagement</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50/30 rounded-lg border border-purple-100/30">
                    <div className="flex justify-center mb-2">
                      <Calendar className="h-6 w-6 text-morgenViolet" />
                    </div>
                    <p className="text-lg font-bold text-morgenViolet mb-1">Risk Reduction</p>
                    <p className="text-sm text-gray-600">Better outcomes</p>
                  </div>
                  <div className="text-center p-4 bg-teal-50/30 rounded-lg border border-teal-100/30">
                    <div className="flex justify-center mb-2">
                      <Check className="h-6 w-6 text-morgenTeal" />
                    </div>
                    <p className="text-lg font-bold text-morgenTeal mb-1">PMPM Savings</p>
                    <p className="text-sm text-gray-600">Measurable ROI</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
